import React, { useState, useRef } from 'react'
import { Paper, Grid, Box, IconButton, TextField, Autocomplete, Select, MenuItem, FormControl, InputLabel, Typography } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrashCan } from '@fortawesome/free-solid-svg-icons'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import PlayCircleIcon from '@mui/icons-material/PlayCircle'
import StopCircleIcon from '@mui/icons-material/StopCircle'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { REACT_APP_API_URL } from '../../config'

const { REACT_APP_SERVER } = process.env
const color = REACT_APP_SERVER === 'ATS' ? '#0973b9' : '#7ebc0a'

export default function FileCard({ file, index, files, destinations, form, setForm, langIndex, greenTheme, inputStyles, inputLabelStyles, selectStyles, group, weekDays, input, isOrdered, compact }) {
  const [audio, setAudio] = useState('')
  const [isPlaying, setIsPlaying] = useState(0)
  const audioRef = useRef(null)

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ 
    id: file.id || index
  })

  const fetchAudio = async (src) => {
    try {
      setIsPlaying(src)
      const link = REACT_APP_API_URL + ((REACT_APP_SERVER === 'Ucom' || REACT_APP_SERVER === 'V-office') ? '/rest' : '')
      const response = await fetch(`${link}/files/audio?file=${src}`)
      const blob = await response.blob()
      const fileURL = URL.createObjectURL(blob)
      setAudio(fileURL)
    } catch (error) {
      console.error("Error fetching audio file:", error)
      setIsPlaying(0)
    }
  }

  const toggleAudio = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause()
        setIsPlaying(0)
      } else {
        audioRef.current.play()
      }
    }
  }

  const handleAudioEnd = () => {
    setIsPlaying(0)
  }

  const style = {
    transition,
    transform: CSS.Transform.toString(transform),
    opacity: isDragging ? 0.5 : 1,
  }

  const schedDayXs = compact ? 6 : 12
  const schedDaySm = compact ? 2 : 6
  const schedTimeXs = compact ? 6 : 12
  const schedTimeSm = compact ? 4 : 6

  const dayFormSx = compact ? { width: '100%', minWidth: 88, maxWidth: 124 } : undefined

  const compactDaySelectSx = compact
    ? {
          ...(selectStyles || {}),
          '& .MuiOutlinedInput-root': { fontSize: '0.8125rem', minHeight: 32 },
          '& .MuiSelect-select': {
              py: '5px',
              px: '8px',
              minHeight: 32,
              display: 'flex',
              alignItems: 'center',
          },
      }
    : selectStyles

  const timeSelectSx = compact
    ? {
          ...(selectStyles || {}),
          '& .MuiOutlinedInput-root': { fontSize: '0.8125rem', minHeight: 32 },
          '& .MuiSelect-select': {
              py: '5px',
              px: '6px',
              minHeight: 32,
              display: 'flex',
              alignItems: 'center',
          },
      }
    : selectStyles

  const timeFormControlSx = compact ? { flex: '0 0 auto', width: 52, minWidth: 52, maxWidth: 52 } : { flex: 1 }

  return (
    <Paper 
      ref={setNodeRef}
      style={style}
      sx={{ 
        p: compact ? 1 : 2, 
        mb: compact ? 1 : 2, 
        border: `1px solid ${greenTheme.primary}`, 
        backgroundColor: '#f9fafb',
        position: 'relative',
        zIndex: isDragging ? 1000 : 1,
      }}
    >
      <Grid container spacing={compact ? 1 : 3}>
        <Grid item xs={1} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Box
            component="span"
            {...attributes}
            {...listeners}
            sx={{
              cursor: 'grab',
              '&:active': {
                cursor: 'grabbing',
              },
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              touchAction: 'none',
              userSelect: 'none',
              WebkitUserSelect: 'none',
            }}
          >
            <IconButton
              size="small"
              sx={{
                color: greenTheme.primary,
                pointerEvents: 'none',
              }}
              aria-label="Drag to reorder"
            >
              <DragIndicatorIcon />
            </IconButton>
          </Box>
        </Grid>
        <Grid 
          item 
          xs={(form.files.length > 1 || file.file) ? 10 : 11}
          sm={(form.files.length > 1 || file.file) ? 10 : 11}
          onClick={(e) => {
            e.stopPropagation()
          }}
        >
          <Autocomplete
            options={files}
            getOptionLabel={(option) => {
              if (typeof option === 'string') return option
              return option.label || option.name || option.fname || ''
            }}
            value={files.find(f => f.fname === file.file || f.name === file.file) || null}
            onChange={(event, newValue) => {
              const updatedFiles = [...form.files]
              updatedFiles[index].file = newValue ? (newValue.fname || newValue.name) : ''
              setForm({ ...form, files: updatedFiles })
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={group.file_label?.[langIndex] || 'File'}
                placeholder={group.file_placeholder?.[langIndex] || 'Select file...'}
                size="small"
                sx={inputStyles}
              />
            )}
          />
        </Grid>
        {(form.files.length > 1 || file.file) && (
          <Grid item xs={12} sm={1} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end',}}>
            {file.file && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation()
                  if (isPlaying === file.file) {
                    toggleAudio()
                  } else {
                    fetchAudio(file.file)
                  }
                }}
                sx={{
                  color: isPlaying === file.file ? '#ef4444' : color,
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.05)',
                  },
                }}
                aria-label={isPlaying === file.file ? 'Stop audio' : 'Play audio'}
              >
                {isPlaying === file.file ? <StopCircleIcon fontSize="medium" /> : <PlayCircleIcon fontSize="medium" />}
              </IconButton>
            )}
            {form.files.length > 1 && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation()
                  const updatedFiles = form.files.filter((_, i) => i !== index)
                  setForm({ ...form, files: updatedFiles })
                }}
                sx={{
                  color: '#ef4444',
                  '&:hover': {
                    backgroundColor: 'rgba(239, 68, 68, 0.1)',
                  },
                }}
                aria-label="Remove file"
              >
                <FontAwesomeIcon icon={faTrashCan} style={{ fontSize: 18 }} />
              </IconButton>
            )}
          </Grid>
        )}
        {/* <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            size="small"
            label="RX"
            placeholder="RX"
            value={file.rx || ''}
            onChange={(e) => {
              const updatedFiles = [...form.files]
              updatedFiles[index].rx = e.target.value
              setForm({ ...form, files: updatedFiles })
            }}
            variant="outlined"
            sx={inputStyles}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            size="small"
            label="TX"
            placeholder="TX"
            value={file.tx || ''}
            onChange={(e) => {
              const updatedFiles = [...form.files]
              updatedFiles[index].tx = e.target.value
              setForm({ ...form, files: updatedFiles })
            }}
            variant="outlined"
            sx={inputStyles}
          />
        </Grid> */}
        <Grid item xs={schedDayXs} sm={schedDaySm} sx={compact ? { display: 'flex', justifyContent: 'flex-start' } : undefined}>
          <FormControl fullWidth size="small" sx={dayFormSx}>
            <InputLabel sx={inputLabelStyles}>
              {group.start_days[langIndex]}
            </InputLabel>
            <Select
              size="small"
              value={file.startDay || ''}
              onChange={(e) => {
                const updatedFiles = [...form.files]
                updatedFiles[index].startDay = e.target.value
                setForm({ ...form, files: updatedFiles })
              }}
              label={group.start_days[langIndex]}
              sx={compactDaySelectSx}
            >
              <MenuItem value="">
                <em>{input.select[langIndex]}</em>
              </MenuItem>
              {weekDays.map((day, dayIndex) => (
                <MenuItem key={dayIndex} value={dayIndex + 1}>
                  {day[langIndex]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={schedDayXs} sm={schedDaySm} sx={compact ? { display: 'flex', justifyContent: 'flex-start' } : undefined}>
          <FormControl fullWidth size="small" sx={dayFormSx}>
            <InputLabel sx={inputLabelStyles}>
              {group.end_days[langIndex]}
            </InputLabel>
            <Select
              size="small"
              value={file.endDay || ''}
              onChange={(e) => {
                const updatedFiles = [...form.files]
                updatedFiles[index].endDay = e.target.value
                setForm({ ...form, files: updatedFiles })
              }}
              label={group.end_days[langIndex]}
              sx={compactDaySelectSx}
            >
              <MenuItem value="">
                <em>{input.select[langIndex]}</em>
              </MenuItem>
              {weekDays.map((day, dayIndex) => (
                <MenuItem key={dayIndex} value={dayIndex + 1}>
                  {day[langIndex]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={schedTimeXs} sm={schedTimeSm}>
          <Box sx={{ display: 'flex', gap: compact ? 0.75 : 1, alignItems: 'center' }}>
            <Typography variant={compact ? 'caption' : 'body2'} sx={{ minWidth: compact ? '44px' : '80px', fontWeight: 500, color: '#64748b', flexShrink: 0, fontSize: compact ? '0.7rem' : undefined }}>
              {group.time_start[langIndex]}:
            </Typography>
            <FormControl size="small" sx={timeFormControlSx}>
              <Select
                size="small"
                value={file.startHour}
                onChange={(e) => {
                  const updatedFiles = [...form.files]
                  updatedFiles[index].startHour = e.target.value
                  setForm({ ...form, files: updatedFiles })
                }}
                displayEmpty
                sx={timeSelectSx}
              >
                <MenuItem value=""><em>HH</em></MenuItem>
                {Array.from({ length: 24 }, (_, i) => (
                  <MenuItem key={i} value={String(i).padStart(2, '0')}>
                    {String(i).padStart(2, '0')}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="body2" sx={{ mx: compact ? 0.15 : 0.5, fontWeight: 600, fontSize: compact ? '0.8rem' : undefined }}>:</Typography>
            <FormControl size="small" sx={timeFormControlSx}>
              <Select
                size="small"
                value={file.startMinute}
                onChange={(e) => {
                  const updatedFiles = [...form.files]
                  updatedFiles[index].startMinute = e.target.value
                  setForm({ ...form, files: updatedFiles })
                }}
                displayEmpty
                sx={timeSelectSx}
              >
                <MenuItem value=""><em>MM</em></MenuItem>
                {Array.from({ length: 60 }, (_, i) => (
                  <MenuItem key={i} value={String(i).padStart(2, '0')}>
                    {String(i).padStart(2, '0')}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Grid>
        <Grid item xs={schedTimeXs} sm={schedTimeSm}>
          <Box sx={{ display: 'flex', gap: compact ? 0.75 : 1, alignItems: 'center' }}>
            <Typography variant={compact ? 'caption' : 'body2'} sx={{ minWidth: compact ? '44px' : '80px', fontWeight: 500, color: '#64748b', flexShrink: 0, fontSize: compact ? '0.7rem' : undefined }}>
              {group.time_end[langIndex]}:
            </Typography>
            <FormControl size="small" sx={timeFormControlSx}>
              <Select
                size="small"
                value={file.endHour}
                onChange={(e) => {
                  const updatedFiles = [...form.files]
                  updatedFiles[index].endHour = e.target.value
                  setForm({ ...form, files: updatedFiles })
                }}
                displayEmpty
                sx={timeSelectSx}
              >
                <MenuItem value=""><em>HH</em></MenuItem>
                {Array.from({ length: 24 }, (_, i) => (
                  <MenuItem key={i} value={String(i).padStart(2, '0')}>
                    {String(i).padStart(2, '0')}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="body2" sx={{ mx: compact ? 0.15 : 0.5, fontWeight: 600, fontSize: compact ? '0.8rem' : undefined }}>:</Typography>
            <FormControl size="small" sx={timeFormControlSx}>
              <Select
                size="small"
                value={file.endMinute}
                onChange={(e) => {
                  const updatedFiles = [...form.files]
                  updatedFiles[index].endMinute = e.target.value
                  setForm({ ...form, files: updatedFiles })
                }}
                displayEmpty
                sx={timeSelectSx}
              >
                <MenuItem value=""><em>MM</em></MenuItem>
                {Array.from({ length: 60 }, (_, i) => (
                  <MenuItem key={i} value={String(i).padStart(2, '0')}>
                    {String(i).padStart(2, '0')}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Grid>
      </Grid>
      {audio && (
        <audio
          onEnded={handleAudioEnd}
          ref={audioRef}
          src={audio}
          style={{ display: 'none' }}
          controls
          autoPlay
        />
      )}
    </Paper>
  )
}

